/**
 * Testi italiani del monitoraggio del sito (ADR 0056), nella pagina Hosting sotto la
 * produzione e sotto la demo: metriche dei pod e del traffico del solo ambiente del
 * progetto, volumi, pod, avvisi e log per chi amministra il set.
 *
 * «Pod» resta com'è: è il nome che compare nei log e negli avvisi, e una parafrasi
 * costringerebbe a indovinare a quale riga si riferiscono.
 */

/** Testi del monitoraggio del sito. */
export const siteMonitoring = {
  /** Titolo della sezione apribile. */
  title: 'Monitoraggio',
  /** Riga sotto il titolo. */
  intro: 'Uso delle risorse, traffico ed errori di questo ambiente.',
  /** Riga per i piani senza monitoraggio. */
  planOnly: 'Il monitoraggio del sito è compreso nei piani Base e Premium.',
  /** Etichetta del selettore del periodo. */
  rangeLabel: 'Periodo',
  /** Periodi. */
  ranges: {
    '1h': '1 ora',
    '24h': '24 ore',
    '7d': '7 giorni',
    '72h': '72 ore',
  },
  /** Comando che rilegge i dati. */
  refresh: 'Aggiorna',
  /** Istante dell'ultima lettura. */
  updatedAt: 'Dati delle {{time}}. Si aggiornano ogni minuto mentre la sezione è aperta.',
  /** Errore generico di lettura. */
  loadFailed: 'Caricamento del monitoraggio non riuscito. Riprova.',
  /** Servizio delle metriche non raggiungibile (503). */
  unavailable: 'I dati del monitoraggio non sono disponibili. Riprova fra poco.',
  /** Rifiuto del piano (409 `plan_no_monitoring`). */
  planRefused: 'Il monitoraggio del sito è compreso nei piani Base e Premium.',
  /** Ambiente non trovato (404). */
  missing: 'Questo ambiente non c’è più. Ricarica la pagina.',
  /** Serie senza punti. */
  noData: 'Nessun dato nel periodo',

  /** Riquadri in alto. */
  tiles: {
    /** Nome accessibile del gruppo di riquadri. */
    label: 'Riepilogo del periodo',
    /** Richieste nel periodo. */
    requests: 'Richieste nel periodo',
    /** Errori 5xx nel periodo. */
    errors5xx: 'Errori 5xx',
    /** Spiegazione degli errori 5xx quando ce ne sono. */
    errors5xxHelp: 'Errori del sito',
    /** Riavvii nel periodo. */
    restarts: 'Riavvii',
    /** Tempo di risposta. */
    latency: 'Tempo di risposta p95',
    /** Nota del tempo di risposta. */
    latencyHelp: 'Ultimo valore',
  },

  /** Grafici. */
  charts: {
    /** Titolo con l'unità dell'asse. */
    withUnit: '{{title}} ({{unit}})',
    /** CPU. */
    cpu: 'CPU',
    /** Memoria. */
    memory: 'Memoria',
    /** Richieste al minuto. */
    requests: 'Richieste al minuto',
    /** Tempo di risposta. */
    latency: 'Tempo di risposta p95',
    /** Traffico di rete. */
    network: 'Traffico di rete',
    /** Serie dell'uso. */
    used: 'Uso',
    /** Linea della richiesta. */
    request: 'Richiesta',
    /** Linea della quota. */
    quota: 'Quota',
    /** Traffico in entrata. */
    rx: 'In entrata',
    /** Traffico in uscita. */
    tx: 'In uscita',
    /** Classi di risposta. */
    classes: {
      '2xx': '2xx riuscite',
      '3xx': '3xx redirezioni',
      '4xx': '4xx errori della richiesta',
      '5xx': '5xx errori del sito',
    },
    /** Nome accessibile della legenda. */
    legend: 'Legenda di {{title}}',
    /** Riassunto di una serie per gli screen reader. */
    seriesSummary: '{{name}}: ultimo valore {{last}}, massimo {{max}}',
    /** Serie senza dati nel riassunto. */
    seriesEmpty: '{{name}}: nessun dato',
    /** Linea di riferimento nel riassunto. */
    referenceSummary: '{{name}} {{value}}',
    /** Nome accessibile del grafico. */
    chartLabel: 'Grafico {{title}}. {{summary}}.',
  },

  /** Volumi. */
  volumes: {
    /** Titolo. */
    title: 'Volumi',
    /** Spazio usato. */
    usage: '{{used}} di {{capacity}} ({{percent}}%)',
    /** Nome accessibile della barra. */
    barLabel: 'Spazio usato da {{name}}',
    /** Nessun volume. */
    empty: 'Nessun volume in questo ambiente.',
  },

  /** Pod. */
  pods: {
    /** Titolo. */
    title: 'Pod',
    /** Colonna del carico di lavoro. */
    workload: 'Carico',
    /** Colonna dello stato. */
    phase: 'Stato',
    /** Colonna «pronto». */
    ready: 'Pronto',
    /** Colonna dei riavvii. */
    restarts: 'Riavvii',
    /** Colonna dell'ultimo errore. */
    lastError: 'Ultimo errore',
    /** Pod pronto. */
    yes: 'Sì',
    /** Pod non pronto. */
    no: 'No',
    /** Nessun pod. */
    empty: 'Nessun pod in questo ambiente.',
    /** Fasi del pod. */
    phases: {
      Running: 'In esecuzione',
      Pending: 'In avvio',
      Succeeded: 'Terminato',
      Failed: 'Fallito',
      Unknown: 'Sconosciuto',
    },
    /** Spiegazione dei motivi d'errore noti. */
    errors: {
      OOMKilled: 'Memoria finita: il processo ha superato la memoria disponibile ed è ripartito.',
      CrashLoopBackOff: 'Il processo si chiude subito dopo l’avvio e riparte di continuo.',
      ImagePullBackOff: 'L’immagine del sito non si scarica. Controlla l’ultimo rilascio.',
      ErrImagePull: 'L’immagine del sito non si scarica. Controlla l’ultimo rilascio.',
      Error: 'Il processo si è chiuso con un errore.',
    },
  },

  /** Avvisi recenti. */
  alerts: {
    /** Titolo. */
    title: 'Avvisi recenti',
    /** Nessun avviso. */
    empty: 'Nessun avviso nel periodo.',
    /** Avviso ancora aperto. */
    open: 'Aperto',
    /** Avviso risolto. */
    resolved: 'Risolto il {{time}}',
    /** Gli avvisi arrivano anche come notifica. */
    notify: 'Gli avvisi arrivano anche come notifica sul telefono.',
    /** Collegamento alle impostazioni delle notifiche. */
    notifyLink: 'Scegli le notifiche',
  },

  /** Log, solo per chi amministra il set. */
  logs: {
    /** Titolo. */
    title: 'Log',
    /** Etichetta del selettore del livello. */
    levelLabel: 'Righe da mostrare',
    /** Solo gli errori. */
    errors: 'Solo errori',
    /** Tutte le righe. */
    all: 'Tutto',
    /** Etichetta del periodo dei log. */
    rangeLabel: 'Periodo dei log',
    /** Conservazione. */
    retention: 'I log restano {{hours}} ore. Segreti, email e indirizzi IP sono oscurati.',
    /** Nessuna riga. */
    empty: 'Nessuna riga nel periodo.',
    /** Elenco tagliato. */
    truncated: 'Mostro le {{count}} righe più recenti.',
    /** Errore di lettura. */
    loadFailed: 'Caricamento dei log non riuscito. Riprova.',
    /** Nome accessibile del blocco delle righe. */
    region: 'Righe del log, dalla più recente',
  },
};

/** Forma dei testi del monitoraggio del sito, per le altre lingue. */
export type SiteMonitoring = typeof siteMonitoring;
